import { Helmet } from "react-helmet-async";

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - TodoTomorrow</title>
        <meta name="description" content="TodoTomorrow's Terms of Service. Review the terms and conditions for using our task management app." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href="https://www.todotomorrow.com/terms" />
        <meta property="og:title" content="Terms of Service - TodoTomorrow" />
        <meta property="og:description" content="TodoTomorrow's Terms of Service. Review the terms and conditions for using our task management app." />
        <meta property="og:url" content="https://www.todotomorrow.com/terms" />
        <meta name="twitter:title" content="Terms of Service - TodoTomorrow" />
        <meta name="twitter:description" content="TodoTomorrow's Terms of Service. Review the terms and conditions for using our task management app." />
      </Helmet>
      <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="container mx-auto max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-8">
          Terms of Service
        </h1>
        
        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-muted-foreground">
            <strong>Effective Date: November 4, 2025</strong>
          </p>
          
          <p className="text-muted-foreground">
            Welcome to TodoTomorrow! These Terms of Service ("Terms") govern your access to and use of the TodoTomorrow mobile application and website (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms.
          </p>
          
          <p className="text-muted-foreground font-semibold">
            PLEASE READ THESE TERMS CAREFULLY BEFORE USING THE SERVICE. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT ACCESS OR USE THE SERVICE.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            1. Acceptance of Terms
          </h2>
          
          <p className="text-muted-foreground">
            By creating an account, downloading our mobile application, or accessing our website, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            2. Eligibility
          </h2>
          
          <p className="text-muted-foreground">
            You must be at least 13 years old (or 16 years old if you are located in the European Economic Area) to use the Service. By using the Service, you represent and warrant that you meet this age requirement. If we discover that you do not meet this requirement, we will terminate your account.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            3. Account Registration and Security
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            3.1 Account Creation
          </h3>
          
          <p className="text-muted-foreground">
            To use TodoTomorrow, you must create an account by providing a valid email address. You agree to:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and promptly update your account information</li>
            <li>Keep your account credentials secure and confidential</li>
            <li>Notify us immediately of any unauthorized access or security breach</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            3.2 Account Responsibility
          </h3>
          
          <p className="text-muted-foreground">
            You are solely responsible for all activities that occur under your account. TodoTomorrow is not liable for any loss or damage arising from your failure to maintain account security.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            3.3 One Account Per User
          </h3>
          
          <p className="text-muted-foreground">
            Each user is permitted one account. Creating multiple accounts to circumvent trial limitations or other restrictions is prohibited and may result in termination of all accounts.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            4. Description of Service
          </h2>
          
          <p className="text-muted-foreground">
            TodoTomorrow is a task management application that allows users to:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Capture todo items throughout the day (primarily in the evening)</li>
            <li>Store tasks locally on their device with cloud synchronization</li>
            <li>Receive a consolidated email containing all open tasks at a scheduled time (default: 6:00 AM)</li>
            <li>Choose between two workflow modes: Fresh Start (daily reset) or Carry Over (persistent tasks with completion tracking)</li>
            <li>Manage task completion and view archived tasks (Carry Over mode only)</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            5. Free Trial and Paid Services
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            5.1 Free Trial
          </h3>
          
          <p className="text-muted-foreground">
            TodoTomorrow offers a free trial that allows you to:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Use the Service for 30 days from your first task creation, OR</li>
            <li>Create up to 100 tasks, whichever comes first</li>
          </ul>
          
          <p className="text-muted-foreground">
            The trial begins when you create your first task, not when you sign up for an account.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            5.2 Premium Purchase
          </h3>
          
          <p className="text-muted-foreground">
            After your trial expires, you must purchase a one-time premium upgrade for $4.99 USD to continue using the Service. Premium access includes:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Unlimited task creation</li>
            <li>Unlimited email delivery</li>
            <li>All features available during the trial period</li>
            <li>Future feature updates at no additional cost</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            5.3 Payment Processing
          </h3>
          
          <p className="text-muted-foreground">
            Payments are processed through:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Stripe</strong> for web-based purchases</li>
            <li><strong>Apple In-App Purchase</strong> for iOS users</li>
            <li><strong>Google Play Billing</strong> for Android users</li>
          </ul>
          
          <p className="text-muted-foreground">
            By making a purchase, you agree to the terms and conditions of the applicable payment processor.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            5.4 One-Time Purchase
          </h3>
          
          <p className="text-muted-foreground">
            TodoTomorrow uses a one-time payment model. There are no subscriptions or recurring charges. Once purchased, you have lifetime access to the Service, subject to these Terms.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            5.5 Pricing Changes
          </h3>
          
          <p className="text-muted-foreground">
            We reserve the right to modify pricing for future users. Price changes will not affect users who have already purchased premium access. Users who purchased at the original price will maintain their access at that price.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            6. Refund Policy
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            6.1 General Refund Policy
          </h3>
          
          <p className="text-muted-foreground">
            Due to the nature of digital products and our generous 30-day/100-task trial period, all sales are generally final. However, we may offer refunds at our sole discretion in the following circumstances:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Technical issues that prevent you from using the Service</li>
            <li>Accidental duplicate purchases</li>
            <li>Purchases made by unauthorized users</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            6.2 Refund Requests
          </h3>
          
          <p className="text-muted-foreground">
            To request a refund, contact us at info@todotomorrow.com within 14 days of purchase with:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Your account email address</li>
            <li>Order/transaction ID</li>
            <li>Reason for the refund request</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            6.3 Platform-Specific Refunds
          </h3>
          
          <p className="text-muted-foreground">
            For purchases made through Apple or Google:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Apple:</strong> Refund requests must be made through Apple's App Store refund process</li>
            <li><strong>Google:</strong> Refund requests must be made through Google Play's refund process</li>
          </ul>
          
          <p className="text-muted-foreground">
            We do not control or process refunds for platform-based purchases.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            7. User Conduct and Acceptable Use
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            7.1 Permitted Use
          </h3>
          
          <p className="text-muted-foreground">
            You may use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Use the Service in any way that violates applicable laws or regulations</li>
            <li>Impersonate any person or entity or misrepresent your affiliation</li>
            <li>Interfere with or disrupt the Service or servers/networks connected to the Service</li>
            <li>Attempt to gain unauthorized access to any portion of the Service</li>
            <li>Use automated systems (bots, scrapers, etc.) to access the Service</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Remove or modify any proprietary notices or labels</li>
            <li>Use the Service to transmit spam, viruses, or malicious code</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            7.2 Content Restrictions
          </h3>
          
          <p className="text-muted-foreground">
            You are solely responsible for the content of your tasks. You agree not to create or store content that:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Is illegal, harmful, threatening, abusive, harassing, or defamatory</li>
            <li>Infringes on intellectual property rights or privacy rights</li>
            <li>Contains viruses, malware, or other harmful code</li>
            <li>Violates any applicable laws or regulations</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            7.3 Enforcement
          </h3>
          
          <p className="text-muted-foreground">
            We reserve the right to investigate violations of these Terms and take appropriate action, including:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Removing prohibited content</li>
            <li>Suspending or terminating accounts</li>
            <li>Reporting illegal activity to law enforcement</li>
            <li>Taking legal action</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            8. Intellectual Property Rights
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            8.1 TodoTomorrow's Intellectual Property
          </h3>
          
          <p className="text-muted-foreground">
            The Service, including its design, features, graphics, code, and all related intellectual property, is owned by TodoTomorrow and protected by copyright, trademark, and other laws. You are granted a limited, non-exclusive, non-transferable license to access and use the Service for personal, non-commercial purposes.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            8.2 Your Content
          </h3>
          
          <p className="text-muted-foreground">
            You retain all rights to the task content you create using the Service. By using the Service, you grant TodoTomorrow a limited, worldwide, non-exclusive license to:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Store and process your content to provide the Service</li>
            <li>Display your content back to you within the Service</li>
            <li>Transmit your content via email as part of the core functionality</li>
          </ul>
          
          <p className="text-muted-foreground">
            We do not claim ownership of your content and will not use it for any purpose other than providing the Service.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            8.3 Feedback
          </h3>
          
          <p className="text-muted-foreground">
            If you provide feedback, suggestions, or ideas about the Service, you grant TodoTomorrow the right to use, modify, and incorporate such feedback without any obligation to compensate you.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            9. Email Communications
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            9.1 Transactional Emails
          </h3>
          
          <p className="text-muted-foreground">
            By using the Service, you consent to receive transactional emails, including:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Daily task list emails (at your scheduled delivery time)</li>
            <li>Account verification and password reset emails</li>
            <li>Trial expiration notifications</li>
            <li>Payment confirmations and receipts</li>
            <li>Service updates and important announcements</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            9.2 Email Deliverability
          </h3>
          
          <p className="text-muted-foreground">
            We make reasonable efforts to ensure email delivery but cannot guarantee that emails will always reach your inbox. Factors affecting delivery include:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Your email provider's spam filters</li>
            <li>Your email account settings</li>
            <li>Temporary service disruptions</li>
          </ul>
          
          <p className="text-muted-foreground">
            You are responsible for:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Maintaining a valid email address</li>
            <li>Checking your spam/junk folder</li>
            <li>Whitelisting noreply@todotomorrow.com (or our sending address)</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            9.3 Opting Out
          </h3>
          
          <p className="text-muted-foreground">
            You cannot opt out of transactional emails while maintaining an active account, as they are essential to the Service. To stop receiving emails, you must delete your account.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            10. Data and Privacy
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            10.1 Privacy Policy
          </h3>
          
          <p className="text-muted-foreground">
            Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your information.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            10.2 Data Storage and Backup
          </h3>
          
          <p className="text-muted-foreground">
            We use industry-standard practices to store and protect your data. However:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>You are responsible for maintaining your own backups of important information</li>
            <li>We are not liable for data loss due to technical failures, service disruptions, or account termination</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            10.3 Data Portability
          </h3>
          
          <p className="text-muted-foreground">
            You may request an export of your task data at any time by contacting info@todotomorrow.com. We will provide your data in a machine-readable format (JSON or CSV) within 30 days.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            11. Service Availability and Modifications
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            11.1 Service Availability
          </h3>
          
          <p className="text-muted-foreground">
            We strive to provide reliable, uninterrupted access to the Service. However, we do not guarantee that:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>The Service will be available at all times</li>
            <li>The Service will be error-free or bug-free</li>
            <li>Data transmission will be secure or uninterrupted</li>
            <li>Emails will always be delivered on time</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            11.2 Maintenance and Downtime
          </h3>
          
          <p className="text-muted-foreground">
            We may perform scheduled or emergency maintenance that temporarily interrupts Service availability. We will provide reasonable notice of scheduled maintenance when possible.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            11.3 Service Modifications
          </h3>
          
          <p className="text-muted-foreground">
            We reserve the right to:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Modify, suspend, or discontinue any feature of the Service</li>
            <li>Update or change these Terms</li>
            <li>Change pricing for new users (existing paid users are unaffected)</li>
          </ul>
          
          <p className="text-muted-foreground">
            We will notify users of material changes via email or in-app notification.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            12. Third-Party Services
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            12.1 Third-Party Integrations
          </h3>
          
          <p className="text-muted-foreground">
            The Service relies on third-party providers, including:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Supabase</strong> (database and authentication)</li>
            <li><strong>SendGrid</strong> (email delivery)</li>
            <li><strong>Stripe</strong> (payment processing)</li>
            <li><strong>Apple/Google</strong> (in-app purchases)</li>
          </ul>
          
          <p className="text-muted-foreground">
            Your use of these services is subject to their respective terms and conditions. We are not responsible for the actions or policies of third-party providers.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            12.2 Third-Party Links
          </h3>
          
          <p className="text-muted-foreground">
            The Service may contain links to third-party websites or services. We are not responsible for the content, privacy practices, or terms of service of any third-party sites.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            13. Disclaimers and Limitations of Liability
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            13.1 Disclaimer of Warranties
          </h3>
          
          <p className="text-muted-foreground">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>IMPLIED WARRANTIES OF MERCHANTABILITY</li>
            <li>FITNESS FOR A PARTICULAR PURPOSE</li>
            <li>NON-INFRINGEMENT</li>
            <li>ACCURACY, RELIABILITY, OR COMPLETENESS</li>
          </ul>
          
          <p className="text-muted-foreground">
            We do not warrant that the Service will meet your requirements or that it will be uninterrupted, secure, or error-free.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            13.2 Limitation of Liability
          </h3>
          
          <p className="text-muted-foreground font-semibold">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, TODOTOMORROW SHALL NOT BE LIABLE FOR ANY:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
            <li>LOSS OF PROFITS, DATA, USE, OR GOODWILL</li>
            <li>SERVICE INTERRUPTIONS OR DATA LOSS</li>
            <li>UNAUTHORIZED ACCESS TO YOUR ACCOUNT</li>
          </ul>
          
          <p className="text-muted-foreground">
            OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM, OR $50 USD, WHICHEVER IS GREATER.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            13.3 Exceptions
          </h3>
          
          <p className="text-muted-foreground">
            Some jurisdictions do not allow the exclusion or limitation of certain warranties or liabilities. In such jurisdictions, the above limitations may not apply to you.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            14. Indemnification
          </h2>
          
          <p className="text-muted-foreground">
            You agree to indemnify, defend, and hold harmless TodoTomorrow, its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Your use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights</li>
            <li>Your content or conduct on the Service</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            15. Termination
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            15.1 Termination by You
          </h3>
          
          <p className="text-muted-foreground">
            You may terminate your account at any time by:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Using the account deletion feature in Settings, or</li>
            <li>Contacting us at info@todotomorrow.com</li>
          </ul>
          
          <p className="text-muted-foreground">
            Upon termination, your data will be permanently deleted within 30 days (see our Privacy Policy for details).
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            15.2 Termination by Us
          </h3>
          
          <p className="text-muted-foreground">
            We may suspend or terminate your account immediately, without prior notice, if:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>You violate these Terms</li>
            <li>You engage in fraudulent or illegal activity</li>
            <li>Your account is inactive for an extended period</li>
            <li>We discontinue the Service</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            15.3 Effect of Termination
          </h3>
          
          <p className="text-muted-foreground">
            Upon termination:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Your right to access the Service immediately ceases</li>
            <li>Your data may be deleted in accordance with our Privacy Policy</li>
            <li>Paid users are not entitled to refunds upon voluntary termination</li>
            <li>Provisions of these Terms that should survive termination will remain in effect</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            16. Dispute Resolution
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            16.1 Governing Law
          </h3>
          
          <p className="text-muted-foreground">
            These Terms shall be governed by and construed in accordance with the laws of the United States and the State of [Your State], without regard to conflict of law principles.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            16.2 Informal Resolution
          </h3>
          
          <p className="text-muted-foreground">
            Before filing a claim, you agree to contact us at info@todotomorrow.com to attempt to resolve the dispute informally. We will work in good faith to resolve disputes within 30 days.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            16.3 Arbitration Agreement
          </h3>
          
          <p className="text-muted-foreground">
            If informal resolution fails, you agree that any dispute arising from these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. The arbitration shall take place in [Your Location] or remotely via video conference.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            16.4 Class Action Waiver
          </h3>
          
          <p className="text-muted-foreground">
            YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            16.5 Exceptions
          </h3>
          
          <p className="text-muted-foreground">
            Either party may seek injunctive relief or other equitable remedies in court to prevent actual or threatened infringement of intellectual property rights or confidentiality obligations.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            17. General Provisions
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            17.1 Entire Agreement
          </h3>
          
          <p className="text-muted-foreground">
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and TodoTomorrow regarding the Service and supersede all prior agreements.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            17.2 Severability
          </h3>
          
          <p className="text-muted-foreground">
            If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            17.3 Waiver
          </h3>
          
          <p className="text-muted-foreground">
            Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            17.4 Assignment
          </h3>
          
          <p className="text-muted-foreground">
            You may not assign or transfer these Terms or your rights under them without our prior written consent. We may assign these Terms without restriction.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            17.5 Force Majeure
          </h3>
          
          <p className="text-muted-foreground">
            We are not liable for any delay or failure to perform resulting from causes beyond our reasonable control, including natural disasters, war, terrorism, labor disputes, or internet/telecommunications failures.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            17.6 Contact Information
          </h3>
          
          <p className="text-muted-foreground">
            For questions about these Terms, please contact us at:
          </p>
          
          <p className="text-muted-foreground">
            <strong>Email:</strong> info@todotomorrow.com<br />
            <strong>Website:</strong> https://todotomorrow.com/terms
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            18. Changes to Terms
          </h2>
          
          <p className="text-muted-foreground">
            We reserve the right to modify these Terms at any time. When we make material changes, we will:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Update the "Effective Date" at the top of this document</li>
            <li>Notify you via email or in-app notification</li>
            <li>Post the updated Terms on our website</li>
          </ul>
          
          <p className="text-muted-foreground">
            Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms. If you do not agree to the new Terms, you must stop using the Service and may delete your account.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            19. Acknowledgment
          </h2>
          
          <p className="text-muted-foreground">
            BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE SERVICE.
          </p>
          
          <p className="text-muted-foreground italic mt-8">
            <em>Last Updated: November 4, 2025</em>
          </p>
          
          <p className="text-muted-foreground italic mt-4">
            <strong>TodoTomorrow</strong> - Evening brain dump → Morning email<br />
            © 2025 TodoTomorrow. All rights reserved.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Terms;
